import { validate as uuidValidate } from 'uuid';
import { salesforceIdRegex } from '../services/utils';
import { FlowQuestion, FlowQuestionRule, QuestionType } from '../dto/qualifying-flow-models';
import { SalesChannel } from '../dto/model';

export function getEnumValueFromString<T extends Record<string, string | number>>(enumObj: T, input: string): T[keyof T] | undefined {
  if (!input || typeof input !== 'string') return undefined;
  const lower = input.toLowerCase();

  const keys = Object.keys(enumObj) as Array<keyof T>;
  const keyMatch = keys.find((k) => String(k).toLowerCase() === lower);
  if (keyMatch) return enumObj[keyMatch];

  const values = Object.values(enumObj) as Array<T[keyof T]>;
  const valueMatch = values.find((v) => typeof v === 'string' && v.toLowerCase() === lower);
  return valueMatch;
}

export type LocalizedText = { [lang: string]: string };

export interface QualifyingAnswers {
  [questionId: string]: string;
}

export interface QualifyingAnswer {
  questionId: string;
  questionText: string;
  answerText: string;
}

// ─── Rule evaluation ─────────────────────────────────────────────────────────

/**
 * Finds the first matching rule for the given answer against the flow-question rules.
 *
 * Dropdown: matches when rule.when.optionKey equals the option key of the selected answer.
 *   The answer value is the display text (DE/EN), so we resolve back to the option key.
 * NumberField: matches when the numeric comparison holds.
 */
function findMatchingRule(
  flowQuestion: FlowQuestion,
  answerValue: string,
  language: string
): FlowQuestionRule | null {
  const { question, rules } = flowQuestion;
  console.log('Finding matching rule for answer', { questionId: question.id, answerValue, language, rules });
  if (!rules || rules.length === 0 || !answerValue) return null;

  if (question.questionType === QuestionType.Dropdown) {
    // Resolve answer display text → option key
    const matchedOption = question.options.find(
      (o) => (o.text?.[language] ?? o.text?.['de'] ?? o.text?.['en'] ?? o.key) === answerValue
    );
    const optionKey = matchedOption?.key;
    if (!optionKey) return null;

    return rules.find((r) => r.when.optionKey === optionKey) ?? null;
  }

  if (question.questionType === QuestionType.NumberField) {
    const numValue = Number.parseFloat(answerValue);
    if (isNaN(numValue)) return null;

    for (const rule of rules) {
      const { operator, value } = rule.when;
      if (operator == null || value == null) continue;
      let matches = false;
      switch (operator) {
        case '>':  matches = numValue > value;  break;
        case '>=': matches = numValue >= value; break;
        case '<':  matches = numValue < value;  break;
        case '<=': matches = numValue <= value; break;
        case '=': matches = numValue === value; break;
      }
      if (matches) return rule;
    }
  }

  return null;
}

// ─── Conditional-display evaluation ──────────────────────────────────────────

/**
 * Returns the effective ordered list of flow questions to present, taking
 * skipNext / goTo actions into account as answers are collected.
 *
 * This is a forward-only simulation: the caller drives it question-by-question
 * and asks "what is the next question index?" after each answer.
 */
export function getNextQuestionIndex(
  flowQuestions: FlowQuestion[],
  currentIndex: number,
  answerValue: string,
  language: string = 'en'
): number | null {
  const flowQuestion = flowQuestions[currentIndex];
  console.log('Getting next question index for', { currentIndex, answerValue, language, questionId: flowQuestion?.question.id });
  if (!flowQuestion) return null;

  const matchedRule = findMatchingRule(flowQuestion, answerValue, language);
  console.log('Matched rule:', matchedRule);

  if (matchedRule) {
    const { goTo, skipNext } = matchedRule.then;
    if (goTo != null) {
      // 1-based absolute position → 0-based index
      const idx = goTo - 1;
      return idx < flowQuestions.length ? idx : null;
    }
    if (skipNext != null) {
      const next = currentIndex + 1 + skipNext;
      return next < flowQuestions.length ? next : null;
    }
  }

  // Default: advance by one
  const next = currentIndex + 1;
  return next < flowQuestions.length ? next : null;
}

// ─── Routing evaluation ───────────────────────────────────────────────────────

/**
 * Determines the SalesChannel based on qualifying question answers and flow rules.
 *
 * Aggregation logic:
 * - Each answered question casts a "vote" for a SalesChannel via its matching rule's Route.
 * - Questions with no matching rule or no Route action do not vote.
 * - Final result: FieldSales only if ALL votes are FieldSales.
 *   Otherwise → DigitalSales.
 * - If no questions vote → DigitalSales.
 */
export function determineLeadSalesChannel(
  flowQuestions: FlowQuestion[],
  answers: QualifyingAnswers,
  language: string = 'en'
): SalesChannel {
  const votes: SalesChannel[] = [];
  console.log('Determining lead sales channel with answers', answers);
  for (const flowQuestion of flowQuestions) {
    const answerValue = answers[flowQuestion.question.id];
    if (!answerValue) continue;

    const rule = findMatchingRule(flowQuestion, answerValue, language);
    if (!rule?.then.route) continue;

    const channel = getEnumValueFromString(SalesChannel, rule.then.route);
    if (channel) votes.push(channel);
  }
  console.log('Channel votes:', votes);

  if (votes.length > 0 && votes.every((v) => v === SalesChannel.FieldSales)) {
    return SalesChannel.FieldSales;
  }

  return SalesChannel.DigitalSales;
};

// ─── Params validation (unchanged) ───────────────────────────────────────────

export const validateCalendarParams = (opportunityId: string, signupId: string, type: string | null): { isValid: boolean; error?: string } => {
  const hasOpportunityId = !!opportunityId && opportunityId !== 'null';
  const hasSignupId = !!signupId;
  const hasType = !!type;
  type = type?.toLowerCase() || null;

  if (!hasOpportunityId && !hasSignupId) {
    return { isValid: false, error: 'Mandatory input parameters are missing in the querystring' };
  }

  // both provided - not allowed
  if (hasOpportunityId && hasSignupId) {
    return { isValid: false, error: 'Only one of OpportunityId or signupId should be provided.' };
  }

  // signupId validation
  if (hasSignupId) {
    if (!uuidValidate(signupId)) {
      return { isValid: false, error: 'The provided signupId is not a valid UUID.' };
    }
    if (hasType && type !== 'leadqueue') {
      return { isValid: false, error: 'type must be "leadqueue" when signupId is provided with a type.' };
    }
  }

  // OpportunityId validation
  if (hasOpportunityId) {
    if (!salesforceIdRegex.test(opportunityId)) {
      return { isValid: false, error: 'The provided opportunityId is not a valid Salesforce ID.' };
    }
    if (type === 'leadqueue') {
      return { isValid: false, error: 'type "leadqueue" is only allowed when signupId is provided.' };
    }
  }

  return { isValid: true };
};
